// Multi-entity G4: when a person's primary employment has ended and another of
// their employments took over, the old company no longer pays them — so its
// outstanding loan / credit repayments are no longer deducted. They are never
// moved automatically: payroll staff see them here and move them, with a
// reason, to the employment that pays the person now (server: audited, one
// payroll carries each repayment). Renders nothing when there is nothing to do.
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowRightLeft } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { usePrompt } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import { engagementsApi, type RepaymentMovePreview } from './engagementsApi';

/** "1 loan (TZS 200,000 left) and 2 credit instalments". Pure. */
export function outstandingLine(p: Pick<RepaymentMovePreview, 'loans' | 'credit_installments'>): string {
  const parts: string[] = [];
  if (p.loans.length) {
    const byCur = new Map<string, number>();
    for (const l of p.loans) byCur.set(l.currency, (byCur.get(l.currency) ?? 0) + Number(l.balance));
    const left = [...byCur.entries()].map(([c, n]) => `${c} ${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`).join(' + ');
    parts.push(`${p.loans.length} ${p.loans.length === 1 ? 'loan' : 'loans'} (${left} left)`);
  }
  if (p.credit_installments.length) {
    parts.push(`${p.credit_installments.length} credit ${p.credit_installments.length === 1 ? 'instalment' : 'instalments'}`);
  }
  return parts.join(' and ');
}

export const RepaymentMoveCallout: React.FC<{ personId: string; personName: string }> = ({ personId, personName }) => {
  const canWrite = useHasPermission('payroll:write');
  const prompt = usePrompt();
  const [preview, setPreview] = useState<RepaymentMovePreview | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setPreview(await engagementsApi.repaymentMovePreview(personId));
    } catch {
      setPreview(null); // not reachable / feature off: nothing to show
    }
  }, [personId]);

  useEffect(() => {
    if (canWrite) void load();
  }, [canWrite, load]);

  if (!canWrite || !preview || !preview.movable) return null;
  const from = preview.from.company_name || 'the previous company';

  const move = async (targetId: string, company: string) => {
    const reason = await prompt({
      title: `Move repayments to ${company}`,
      message: `${outstandingLine(preview)} for ${personName} are still set to be deducted by ${from}, which no longer pays them. After the move, ${company}'s payroll deducts them instead.`,
      label: 'Reason',
      multiline: true,
      required: true,
      minLength: 3,
      confirmLabel: 'Move repayments',
    });
    if (!reason) return;
    setBusy(true);
    try {
      await engagementsApi.moveRepayments(personId, targetId, reason);
      toast.success(`Repayments now deducted by ${company}.`);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not move the repayments.'), { duration: 7000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-warning bg-warning-soft p-3 space-y-2" data-testid="repayment-move">
      <p className="text-sm text-warning-strong">
        {outstandingLine(preview)} {preview.loans.length + preview.credit_installments.length === 1 ? 'is' : 'are'} still set to be
        deducted by {from}, which no longer pays {personName}. Nothing is deducted until you move them.
      </p>
      <div className="flex flex-wrap gap-2">
        {preview.targets.map((t) => (
          <Button key={t.employee_id} size="sm" variant="outline" className="gap-1" disabled={busy}
            onClick={() => void move(t.employee_id, t.company_name || 'the new company')}>
            <ArrowRightLeft className="w-4 h-4" /> Move repayments to {t.company_name || 'the new company'}
          </Button>
        ))}
      </div>
    </div>
  );
};
